import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { ChevronLeft, X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { Kbd } from '@/components/ui/kbd'
import { Tooltip } from '@/components/ui/tooltip'

/**
 * The inspector surface.
 *
 * Two layouts, one component:
 *
 *  - **md and up** it is a right-hand panel — bg-s1, a hairline on its left
 *    edge, a real shadow because it genuinely floats over the table.
 *  - **below md it is the whole screen.** A 720px panel on a 360px phone is not
 *    a narrower panel, it is a broken one: the content is clipped, the close
 *    button lands off-canvas and the page gains a horizontal scrollbar. So the
 *    phone gets `inset-0`, no radius, no border, and a back chevron instead of
 *    an X — because full-screen reads as a *page*, and you leave a page by
 *    going back.
 *
 * Radix supplies the focus trap, the Escape handler and the scroll lock;
 * everything here is presentation.
 */
export function Drawer(p: {
  open: boolean
  onOpenChange: (o: boolean) => void
  title: React.ReactNode
  description?: React.ReactNode
  children: React.ReactNode
  footer?: React.ReactNode
  /**
   * Rendered on the trailing edge of the header, before the close button.
   *
   * For identity — who this panel is about — rather than for controls. It sits
   * outside `DialogPrimitive.Title` on purpose: the title is the dialog's
   * accessible NAME, and folding a customer id, an email and a ticking clock
   * into it would have a screen reader announce all of them on every open.
   */
  headerExtra?: React.ReactNode
  widthClass?: string
}) {
  return (
    <DialogPrimitive.Root open={p.open} onOpenChange={p.onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]',
            'transition-opacity duration-150 ease-out starting:opacity-0'
          )}
        />
        <DialogPrimitive.Content
          /*
           * Open with focus on the PANEL, not on its first button.
           *
           * Radix focuses the first tabbable element on open, and in this header
           * that is the close X — whose tooltip therefore opened on every open,
           * over whatever the header shows beside it (the run drawer's session
           * clock), before anyone had touched a thing. The panel is the better
           * first stop anyway: it is the dialog, so a screen reader announces it
           * by its title, and Tab goes on to the first control from there. A
           * field inside that asks for focus with `autoFocus` still gets it —
           * Radix skips this step when focus is already inside.
           *
           * `outline-none` because the panel is a focus target, not a control:
           * opened from the keyboard it would otherwise wear the focus ring
           * around the whole screen.
           */
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            if (e.currentTarget instanceof HTMLElement) e.currentTarget.focus({ preventScroll: true })
          }}
          className={cn(
            'fixed inset-0 z-50 flex flex-col bg-s1 outline-none',
            // Safe-area insets belong on the outermost box: the header and the
            // footer then sit inside the notch/home-indicator, and the padding
            // itself is panel-coloured rather than a black band.
            'pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]',
            'md:inset-y-0 md:right-0 md:left-auto md:border-l md:border-line md:pt-0 md:pb-0 md:shadow-e3',
            'transition-transform duration-200 ease-out starting:translate-x-full',
            'w-full',
            // Callers pass a `md:`-prefixed width. The `max-md:w-full` below is
            // the seatbelt: an unprefixed width from a caller cannot make the
            // panel wider than a phone.
            p.widthClass ?? 'md:w-[560px]',
            'max-md:w-full max-md:max-w-full'
          )}
        >
          <header
            className={cn(
              'flex min-h-[60px] flex-none items-center gap-2 border-b border-line px-4 md:px-5'
            )}
          >
            {/* Phone: a back chevron, 44px, on the leading edge. */}
            <DialogPrimitive.Close
              aria-label="Back"
              className={cn(
                '-ml-2 flex size-11 flex-none items-center justify-center rounded-md text-muted',
                'transition-colors duration-100 hover:bg-s3 hover:text-fg md:hidden'
              )}
            >
              <ChevronLeft className="size-5" aria-hidden="true" />
            </DialogPrimitive.Close>

            <div className="min-w-0 flex-1 py-2.5">
              <DialogPrimitive.Title className="truncate font-display text-[16px] leading-[22px] font-semibold text-fg">
                {p.title}
              </DialogPrimitive.Title>
              {p.description !== undefined && p.description !== null ? (
                <DialogPrimitive.Description className="mt-0.5 truncate text-[13px] leading-[18px] text-muted">
                  {p.description}
                </DialogPrimitive.Description>
              ) : (
                // Radix warns when `aria-describedby` has no target; give it a
                // real one instead of switching the association off.
                <DialogPrimitive.Description className="sr-only">
                  Detail panel
                </DialogPrimitive.Description>
              )}
            </div>

            {p.headerExtra !== undefined && p.headerExtra !== null ? (
              <div className="hidden min-w-0 shrink items-center md:flex">{p.headerExtra}</div>
            ) : null}

            {/*
              Desktop: the conventional X, on the trailing edge.

              Its hint opens BELOW it. To the left it sat on the header's
              trailing edge, which is where `headerExtra` puts who the panel is
              about — so pointing at the way out covered the one thing worth a
              last look before leaving. It names the key too, the faster way.
            */}
            <Tooltip
              side="bottom"
              content={
                <span className="flex items-center gap-2">
                  Close
                  <Kbd>Esc</Kbd>
                </span>
              }
            >
              <DialogPrimitive.Close
                aria-label="Close panel"
                className={cn(
                  '-mr-1.5 hidden size-8 flex-none items-center justify-center rounded-md text-muted',
                  'transition-colors duration-100 hover:bg-s3 hover:text-fg md:flex'
                )}
              >
                <X className="size-[18px]" aria-hidden="true" />
              </DialogPrimitive.Close>
            </Tooltip>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{p.children}</div>

          {p.footer ? (
            <footer
              className={cn(
                'flex flex-none items-center justify-end gap-2 border-t border-line bg-s1 px-4 py-3 md:px-5',
                // Thumb-sized actions on a phone, without every caller having
                // to know that.
                'max-md:[&>button]:h-11 max-md:[&>button]:flex-1 max-md:[&>button]:text-[14px]'
              )}
            >
              {p.footer}
            </footer>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
